import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/**
 * The one field surface for the whole platform — background, border, height, focus ring.
 *
 * Exported because a few screens build their own element (a native <select> inside a table cell,
 * a page-local `fieldCls`) and every hand-rolled copy of these classes has eventually drifted:
 * the usual symptom is an omitted `bg-background`, which leaves the control transparent and
 * unreadable on a dark card. Import this instead of retyping it.
 */
export const inputClass =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors ' +
  'placeholder:text-muted-foreground ' +
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/** Checkbox/radio counterpart — same border token, small square, brand-tinted check. */
export const checkboxClass = 'h-4 w-4 rounded border-input accent-primary disabled:opacity-50';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(
        inputClass,
        'file:border-0 file:bg-transparent file:text-sm file:font-medium',
        className
      )}
      {...props}
    />
  )
);
Input.displayName = 'Input';
